import { HarpValidators } from '@shared/utils/validators';
import { InputConfig } from '@shared/models/input-config.model';
import { TEXT_CONSTANTS, REGEX, DEFAULT_MIN_DATE } from '@shared/constants';

const { ERROR_MESSAGE_REQUIRED } = TEXT_CONSTANTS;

const {
  FIRST_NAME_REGEX,
  LAST_NAME_REGEX,
  MIDDLE_NAME_REGEX,
  EMAIL_REGEX,
  EMAIL_REGEX_STRICT,
  PHONE_NUMBER_REGEX,
  FOREIGN_PHONE_NUMBER_REGEX,
  FOREIGN_ADDRESS_LINE_1_REGEX,
  FOREIGN_ADDRESS_LINE_2_REGEX,
  FOREIGN_CITY_REGEX,
  ADDRESS_LINE_1_REGEX,
  ADDRESS_LINE_2_REGEX,
  CITY_REGEX,
  ZIP_CODE_REGEX,
  ZIP_CODE_EXTENSION_REGEX,
  SSN_REGEX,
  REFERENCE_NUMBER_REGEX,
  LAST_4_SSN_REGEX,
} = REGEX;

export const FIRST_NAME_INPUT_CONFIG: InputConfig = {
  required: true,
  type: 'text',
  label: 'Legal First Name',
  name: 'firstName',
  maxlength: 32,
  popover: {
    text: 'If your first name consists of two names, please enter the second name in the Middle Name field.',
    placement: 'right',
  },
};

export const FIRST_NAME_VALIDATORS = [
  HarpValidators.required('First Name is required'),
  HarpValidators.pattern(FIRST_NAME_REGEX, 'Invalid First Name'),
];

export const LAST_NAME_INPUT_CONFIG: InputConfig = {
  required: true,
  type: 'text',
  label: 'Legal Last Name',
  name: 'lastName',
  maxlength: 32,
  popover: {
    text: 'You may only use an apostrophe following the letters D, L, and O.',
    placement: 'right',
  },
};

export const LAST_NAME_VALIDATORS = [
  HarpValidators.required('Last Name is required'),
  HarpValidators.pattern(LAST_NAME_REGEX, 'Invalid Last Name'),
  HarpValidators.minLength(2),
];

export const MIDDLE_NAME_INPUT_CONFIG: InputConfig = {
  type: 'text',
  label: 'Middle Name',
  name: 'middleName',
  maxlength: 32,
};

export const MIDDLE_NAME_VALIDATORS = [
  HarpValidators.pattern(MIDDLE_NAME_REGEX, 'Invalid Middle Name'),
];

export const DATE_OF_BIRTH_INPUT_CONFIG: InputConfig = {
  required: true,
  type: 'date',
  label: 'Date of Birth',
  name: 'dob',
  popover: {
    text: 'Please use mm/dd/yyyy format.',
    placement: 'right',
  },
};

export const DATE_OF_BIRTH_VALIDATORS = today => [
  HarpValidators.required('Date of Birth is required'),
  HarpValidators.minDate(DEFAULT_MIN_DATE),
  HarpValidators.maxDate(today),
];

export const BUSINESS_EMAIL_ADDRESS_INPUT_CONFIG: InputConfig = {
  required: true,
  type: 'text',
  label: 'Business Email Address',
  name: 'businessEmail',
  popover: {
    text: 'Enter your business email address.',
    placement: 'right',
  },
};

export const BUSINESS_EMAIL_ADDRESS_VALIDATORS = [
  HarpValidators.required('Business Email Address is required'),
  HarpValidators.pattern(EMAIL_REGEX_STRICT, 'Invalid Business Email'),
];

export const CONFIRM_BUSINESS_EMAIL_ADDRESS_INPUT_CONFIG: InputConfig = {
  required: true,
  type: 'text',
  label: 'Confirm Business Email Address',
  name: 'confirmBusinessEmail',
  preventCopyPaste: true,
};

export const CONFIRM_BUSINESS_EMAIL_ADDRESS_VALIDATORS = email => [
  HarpValidators.mustEqual(email, 'Your emails must match'),
];

export const PERSONAL_EMAIL_ADDRESS_INPUT_CONFIG: InputConfig = {
  required: true,
  type: 'text',
  label: 'Personal Email Address',
  name: 'personalEmail',
  popover: {
    text: 'Enter your personal email address.',
    placement: 'right',
  },
};

export const PERSONAL_EMAIL_ADDRESS_VALIDATORS = [
  HarpValidators.required('Personal Email Address is required'),
  HarpValidators.pattern(EMAIL_REGEX_STRICT, 'Invalid Email'),
];

export const CONFIRM_PERSONAL_EMAIL_ADDRESS_INPUT_CONFIG: InputConfig = {
  required: true,
  type: 'text',
  label: 'Confirm Personal Email Address',
  name: 'confirmPersonalEmail',
  preventCopyPaste: true,
};

export const CONFIRM_PERSONAL_EMAIL_ADDRESS_VALIDATORS = email => [
  HarpValidators.required('Confirm Personal Email Address is required'),
  HarpValidators.mustEqual(email, 'Your personal emails must match'),
];

export const PERSONAL_PHONE_NUMBER_INPUT_CONFIG: InputConfig = {
  required: true,
  type: 'text',
  label: 'Personal Phone Number',
  name: 'personalPhoneNumber',
  mask: '(000) 000-0000',
  popover: {
    text: 'Enter your personal phone number, not your work phone number, to prevent failing Remote Identity Proofing.',
    placement: 'right',
  },
};

export const PERSONAL_PHONE_NUMBER_VALIDATORS = [
  HarpValidators.required('Personal Phone Number is required'),
  HarpValidators.pattern(PHONE_NUMBER_REGEX, 'Invalid Phone Number'),
];

export const BUSINESS_PHONE_NUMBER_INPUT_CONFIG: InputConfig = {
  type: 'text',
  label: 'Business Phone Number',
  name: 'businessPhoneNumber',
  mask: '(000) 000-0000',
  popover: {
    text: 'Enter your business phone number.',
    placement: 'right',
  },
};

export const BUSINESS_PHONE_NUMBER_VALIDATORS = [
  HarpValidators.pattern(PHONE_NUMBER_REGEX, 'Invalid Phone Number'),
];

export const IS_US_ADDRESS_INPUT_CONFIG: InputConfig = {
  type: 'switch',
  label: 'Is your address in the United States?',
  required: true,
  name: 'isUSAddress',
  switchConfig: {
    left: {
      label: 'Yes',
      value: 1,
    },
    right: {
      label: 'No',
      value: 0,
    },
  },
};

export const ADDRESS_LINE_1_INPUT_CONFIG: InputConfig = {
  required: true,
  type: 'text',
  label: 'Home Address Line 1',
  name: 'addressLine1',
  maxlength: 60,
  popover: {
    text: 'Enter your home address, not your work address, otherwise you may fail remote identity proofing. If you have recently moved within the past 30 days, you may need to use your old home address.',
    placement: 'right',
  },
};

export const ADDRESS_LINE_1_VALIDATORS = [
  HarpValidators.required('Home Address Line 1 is required'),
  HarpValidators.pattern(ADDRESS_LINE_1_REGEX, 'Invalid Home Address Line 1'),
];

export const ADDRESS_LINE_2_INPUT_CONFIG: InputConfig = {
  type: 'text',
  label: 'Home Address Line 2',
  name: 'addressLine2',
  maxlength: 60,
};

export const ADDRESS_LINE_2_VALIDATORS = [
  HarpValidators.pattern(ADDRESS_LINE_2_REGEX, 'Invalid Home Address Line 2'),
];

export const CITY_INPUT_CONFIG: InputConfig = {
  required: true,
  type: 'text',
  label: 'City',
  name: 'city',
  maxlength: 38,
};

export const CITY_IN_US_VALIDATORS = [
  HarpValidators.required('City is required'),
  HarpValidators.pattern(CITY_REGEX, 'Invalid City '),
];

export const CITY_NOT_IN_US_VALIDATORS = [
  HarpValidators.pattern(FOREIGN_CITY_REGEX, 'Invalid City '),
];

export const STATE_INPUT_CONFIG: InputConfig = {
  required: true,
  type: 'select',
  label: 'State',
  name: 'state',
};

export const STATE_VALIDATORS = [HarpValidators.required('State is required')];

export const COUNTRY_INPUT_CONFIG: InputConfig = {
  required: true,
  type: 'select',
  label: 'Country',
  name: 'country',
};

export const COUNTRY_VALIDATORS = [
  HarpValidators.required('Country is required'),
];

export const ZIP_CODE_INPUT_CONFIG: InputConfig = {
  required: true,
  type: 'text',
  label: 'Zip Code',
  name: 'zipCode',
  maxlength: 5,
};

export const ZIP_CODE_VALIDATORS = [
  HarpValidators.required('ZIP Code is required'),
  HarpValidators.pattern(ZIP_CODE_REGEX, 'Invalid ZIP Code'),
];

export const ZIP_CODE_EXTENSION_INPUT_CONFIG: InputConfig = {
  type: 'text',
  label: 'Zip Code Extension',
  name: 'zipCodeExt',
  maxlength: 4,
};

export const ZIP_CODE_EXTENSION_VALIDATORS = [
  HarpValidators.pattern(
    ZIP_CODE_EXTENSION_REGEX,
    'Invalid ZIP Code Extension',
  ),
];

export const SSN_INPUT_CONFIG: InputConfig = {
  required: true,
  type: 'password',
  label: 'Social Security Number',
  name: 'ssn',
  maxlength: 11,
  popover: {
    text: "SSN is used to generate a list of personal questions for you to answer to verify your identity remotely. Only the last 4 digits are stored. Don't want to enter your SSN? Initiate Manual Proofing.",
    placement: 'right',
  },
};

export const SSN_VALIDATORS = [
  HarpValidators.required('SSN is required'),
  HarpValidators.pattern(SSN_REGEX, 'Invalid SSN'),
  HarpValidators.minLength(9),
];

export const FOREIGN_ADDRESS_LINE_1_INPUT_CONFIG: InputConfig = {
  required: true,
  type: 'text',
  maxlength: 128,
  name: 'foreignAddressLine1',
  label: 'Foreign Address Line 1',
};

export const FOREIGN_ADDRESS_LINE_1_VALIDATORS = [
  HarpValidators.required('Foreign Address Line 1 is required'),
  HarpValidators.pattern(
    FOREIGN_ADDRESS_LINE_1_REGEX,
    'Invalid Foreign Address Line 1',
  ),
];

export const FOREIGN_ADDRESS_LINE_2_INPUT_CONFIG: InputConfig = {
  type: 'text',
  maxlength: 128,
  name: 'foreignAddressLine2',
  label: 'Foreign Address Line 2',
};

export const FOREIGN_ADDRESS_LINE_2_VALIDATORS = [
  HarpValidators.pattern(
    FOREIGN_ADDRESS_LINE_2_REGEX,
    'Invalid Foreign Address Line 2',
  ),
];

export const FOREIGN_PHONE_NUMBER_INPUT_CONFIG: InputConfig = {
  name: 'personalPhoneNumber',
  type: 'text',
  label: 'Foreign Phone Number',
  popover: {
    text: 'Enter your personal phone number, not your work phone number, to prevent failing Remote Identity Proofing.',
    placement: 'right',
  },
};

export const FOREIGN_PHONE_NUMBER_VALIDATORS = [
  HarpValidators.pattern(FOREIGN_PHONE_NUMBER_REGEX, 'Invalid Phone Number'),
];

export const RETRY_USER_ID_INPUT_CONFIG: InputConfig = {
  name: 'retryUserId',
  type: 'text',
  label: 'User ID',
  required: true,
  maxlength: 100,
};

export const RETRY_USER_ID_VALIDATORS = userIdRegex => [
  HarpValidators.required('User ID is required'),
  HarpValidators.pattern(userIdRegex, 'Invalid User ID'),
];

export const RETRY_PASSWORD_INPUT_CONFIG: InputConfig = {
  name: 'retryPassword',
  type: 'password',
  label: 'Password',
  required: true,
};

export const RETRY_PASSWORD_VALIDATORS = passwordRegex => [
  HarpValidators.required('Password is required'),
  HarpValidators.pattern(passwordRegex, 'Invalid Password'),
];

export const RETRY_RIDP_OPTIONS_INPUT_CONFIG: InputConfig = {
  name: 'retryRIDPOptions',
  type: 'radio',
  label: 'Retry Remote Proofing Options',
  showLabel: false,
  options: [
    {
      label: 'I know the last 4 digits of my SSN and date of birth',
      value: true,
      checked: true,
    },
    {
      label: 'I know my Reference Number',
      value: false,
    },
  ],
};

export const RETRY_LAST_4_OF_SSN_INPUT_CONFIG: InputConfig = {
  name: 'retrySSN',
  type: 'password',
  label: 'Last 4 digits of SSN',
  maxlength: 4,
  required: true,
};

export const RETRY_LAST_4_OF_SSN_VALIDATORS = [
  HarpValidators.required('Last 4 digits of SSN is required'),
  HarpValidators.minLength(4),
  HarpValidators.pattern(LAST_4_SSN_REGEX, 'Invalid SSN'),
];

export const RETRY_DATE_OF_BIRTH_INPUT_CONFIG: InputConfig = {
  required: true,
  type: 'date',
  label: 'Date of Birth',
  name: 'retryDob',
};

export const RETRY_DATE_OF_BIRTH_VALIDATORS = [
  HarpValidators.required('Date of Birth is required'),
];

export const RETRY_REFERENCE_NUMBER_INPUT_CONFIG: InputConfig = {
  name: 'retryReferenceId',
  label: 'Reference Number',
  type: 'text',
  required: true,
};

export const RETRY_REFERENCE_NUMBER_VALIDATORS = [
  HarpValidators.required('Reference Number is required'),
  HarpValidators.pattern(REFERENCE_NUMBER_REGEX, 'Invalid Reference Number'),
];

export const FARS_REFERENCE_NUMBER_INPUT_CONFIG: InputConfig = {
  name: 'referenceId',
  label: 'Reference Label',
  type: 'text',
  required: true,
};

export const FARS_REFERENCE_NUMBER_VALIDATORS = [
  HarpValidators.required('Reference Number is required'),
  HarpValidators.pattern(REFERENCE_NUMBER_REGEX, 'Invalid Reference Number'),
];

export const FARS_EMAIL_INPUT_CONFIG: InputConfig = {
  name: 'farsEmail',
  type: 'text',
  label: 'Email Address',
  required: true,
};

export const FARS_EMAIL_VALIDATORS = [
  HarpValidators.required('Email Address is required'),
  HarpValidators.pattern(EMAIL_REGEX, 'Invalid Email Address'),
];

export const RECAPTCHA_VALIDATORS = [
  HarpValidators.required(ERROR_MESSAGE_REQUIRED('reCAPTCHA')),
];

export const COUNTRY_CHANGE_FIELDS_TO_RESET = [
  'city',
  'country',
  'zipCode',
  'zipCodeExt',
  'state',
  'addressLine1',
  'addressLine2',
  'foreignAddressLine1',
  'foreignAddressLine2',
];

export enum FARS_STATUS {
  CONTINUE = 'CONTINUE',
  STOP = 'STOP',
  INVESTIGATE = 'INVESITGATE',
  NODECISION = 'NODECISION',
}
